import { tradeRequestApi } from 'apiConnector/quote/trade';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, startWith, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { FunkyAction } from 'shared/middlewares/epic/epicMiddleware.factory';
import { Action } from 'state/actions';
import {
  addonManagementQuoteTradeAcked,
  addonManagementQuoteTradeDone,
  addonManagementQuoteTradeFailed,
  addonManagementQuoteTradeRejected,
  addonManagementQuoteTradeRequestRejected,
  addonManagementQuoteTradeSent,
} from 'state/addonManagementForm/addonManagementForm.action';
import * as actionTypes from 'state/addonManagementForm/addonManagementForm.actionTypes';
import type { TradeExecReport } from 'state/quotes/trades/trades.model';
import type { EpicWithThunk } from 'state/state.common';
import { throwOnTimeout, wrapError } from 'utils/rxjs';
import { fatEventApiConnector } from 'utils/streaming/api';
import { tradeExecutionReport } from 'utils/streaming/messages';

function handleTradeReport(
  formId: string,
  quoteId: string,
  { status, errors, execMessages }: TradeExecReport,
): Observable<FunkyAction> {
  switch (status) {
    case 'FULLY_EXECUTED':
      logAnalytics.trackEvent('Addon Management Workspace', 'Trade done');
      return of(addonManagementQuoteTradeDone(formId, quoteId, 'FULLY_EXECUTED', execMessages));
    case 'CANCELLED':
    case 'EXPIRED':
    case 'FAILED':
    case 'REJECTED':
      return of(addonManagementQuoteTradeRejected(formId, quoteId, errors!));
    default:
      return EMPTY;
  }
}

function handleError(error: Error, formId: string, lastQuoteId: string): Observable<Action> {
  if (apiErrorHelper.isHttpError(error) && error.status === 400) {
    return of(addonManagementQuoteTradeRequestRejected(formId, lastQuoteId, error.response));
  }
  return of(
    addonManagementQuoteTradeFailed(
      formId,
      lastQuoteId,
      apiErrorHelper.toApiErrorMessage(error, {
        timeoutErrorMessage: 'trade.errorMessages.requestTimeout',
      }),
      error,
    ),
  );
}

export function handleTradeAckEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, _, { oldHttpClient, getSgmeConfiguration, streaming }) =>
    action$.pipe(
      ofType(actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_ACKED),
      mergeMap(({ formId, quoteId }) => {
        const tradeExecReportTimeoutMs = getSgmeConfiguration().tradeExecReportTimeoutMs;
        const tradeFailed$ = action$.pipe(
          ofType(actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_FAILED),
          filter(a => a.formId === formId),
        );
        return tradeExecutionReport(streaming).pipe(
          takeUntil(tradeFailed$),
          filter(r => r.quoteId === quoteId),
          mergeMap(lightEvent =>
            fatEventApiConnector(oldHttpClient)
              .getFatEvent<TradeExecReport>(lightEvent)
              .pipe(
                mergeMap(report => handleTradeReport(formId, quoteId, report)),
                wrapError(
                  error => new apiErrorHelper.EpicApiError('trade.errorMessages.executionReportTechnicalError', error),
                  appScheduler,
                ),
              ),
          ),
          take(1),
          throwOnTimeout(
            tradeExecReportTimeoutMs,
            new apiErrorHelper.TimeoutError(tradeExecReportTimeoutMs, 'trade.errorMessages.executionReportTimeout'),
            appScheduler,
          ),
          startWith(addonManagementQuoteTradeSent(formId, quoteId)),
          catchError(error => handleError(error, formId, quoteId)),
        );
      }),
    );
}

export function requestTradeEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    {
      oldHttpClient,
      selectors: { getAddonManagementFormLastQuoteId, getAddonManagementTradeRequest },
      getSgmeConfiguration,
    },
  ) =>
    action$.pipe(
      ofType(actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_REQUESTED),
      withLatestFrom(state$),
      mergeMap(([{ formId }, state]) => {
        const lastQuoteId = getAddonManagementFormLastQuoteId(state, formId)!;
        const tradeRequest = getAddonManagementTradeRequest(state, formId);
        const tradeAckTimeoutMs = getSgmeConfiguration().tradeAckTimeoutMs;
        return tradeRequestApi(oldHttpClient, tradeAckTimeoutMs)
          .requestTrade(tradeRequest)
          .pipe(
            map(() => addonManagementQuoteTradeAcked(formId, lastQuoteId)),
            catchError(error => handleError(error, formId, lastQuoteId)),
          );
      }),
    );
}
